import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useCarga } from '../carga.js'
import { obtenerRepositorio } from '../datos/index.js'
import type { MesaPublica, RondaPublica } from '../datos/index.js'
import { plural } from '../formato.js'
import { ESPERA_CACHE_PUBLICA_MS, useAvisosDelTorneo, useLlamadaEspaciada } from '../tiempo-real.js'
import { Cargando, Problema, Vacio } from './Estados.js'
import { Icono } from './Icono.js'
import { MesaEnJuego } from './MesaEnJuego.js'
import { PlanillaDeMesa } from './PlanillaDeMesa.js'

/**
 * El tablero del salón, en el teléfono.
 *
 * Contesta la pregunta que la web no contestaba: no «dónde juego yo», que ya
 * tiene su pestaña, sino qué está pasando en el resto de las mesas. En el club
 * eso se mira caminando entre las mesas o parándose frente a la planilla de la
 * pared; acá se mira sentado, y por eso las tres piezas son elegir la ronda,
 * buscar a alguien y encontrarse a uno mismo.
 *
 * Las mesas se piden de a una ronda y no todas juntas. En un torneo de
 * cuatrocientos son cien mesas por ronda: traer las nueve rondas para dibujar
 * una sería bajar cuatro mil nombres al teléfono de alguien que está mirando
 * cuatrocientos.
 *
 * No pregunta cada tantos segundos, y es por lo mismo: cuatrocientos teléfonos
 * preguntando cada diez segundos son cuarenta pedidos por segundo contra un
 * servidor de una vCPU. Lo que hace es escuchar los avisos del torneo por el
 * socket (ver `tiempo-real.ts`) y volver a pedir cuando algo de ESTA ronda
 * cambió. Con freno: las manos se anotan cada pocos segundos en una ronda de
 * cien mesas, y la lista entera se vuelve a pedir como mucho cada
 * `FRENO_MANOS_MS` (`FRENO_MANOS_PUBLICO_MS` en la vitrina). Lo que cambia la
 * ronda entera (se armó, se cerró) se pide enseguida. El botón «Actualizar» sigue estando para quien no quiere esperar.
 *
 * La planilla mano a mano de UNA mesa, que se despliega debajo de su tarjeta,
 * no tiene freno: la mira poca gente y reacciona a cada mano (ver
 * `PlanillaDeMesa`). En la mesa propia, la tarjeta y la planilla ofrecen abrir
 * la libreta de anotación, para no tener que volver a la pestaña «Mi mesa».
 */

/** Cada cuánto, como mucho, se vuelve a pedir la ronda por avisos de manos. */
const FRENO_MANOS_MS = 30_000
/**
 * Lo mismo desde la vitrina, que puede ir más rápido porque no le cuesta al
 * servidor: la lista sale de la caché del borde (ver `cloudflare/cache-publica.ts`
 * en el API), así que miles mirando la ronda son un pedido por centro de datos
 * cada dos segundos y no miles. Con sesión no hay caché y cada teléfono llega
 * hasta la base, por eso ahí sigue el freno largo.
 */
const FRENO_MANOS_PUBLICO_MS = 5_000
/** Lo mismo para los avisos que cambian la ronda entera, que son pocos. */
const FRENO_RONDA_MS = 3_000

/** Sin acentos y en minúsculas: nadie escribe «Núñez» con la tilde al buscar. */
function normalizar(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
}

/**
 * Con qué ronda se abre la pestaña.
 *
 * La que se está jugando, que es la que alguien quiere mirar mientras se juega.
 * Si no hay ninguna abierta —el hueco entre que se cierra una ronda y se arma la
 * siguiente— es la última que tenga mesas: dejar la pantalla vacía en ese rato
 * haría parecer que el torneo se apagó.
 */
function rondaDeArranque(rondas: readonly RondaPublica[]): string | null {
  const conMesas = rondas.filter((r) => r.mesas > 0)
  const abierta = conMesas.find((r) => r.estado !== 'cerrada')
  return (abierta ?? conMesas[conMesas.length - 1])?.rondaId ?? null
}

export function MesasDeRonda({
  torneoId,
  rondas,
  publico = false,
  alAnotar,
}: {
  torneoId: string
  rondas: RondaPublica[]
  /** Desde la vitrina: pide por las rutas sin cuenta y no hay mesa propia. */
  publico?: boolean
  /** Abre la libreta de una mesa. Sin esto (el torneo no la usa) no se ofrece. */
  alAnotar?: (mesaId: string) => void
}) {
  const arranque = rondaDeArranque(rondas)
  const [rondaId, setRondaId] = useState<string | null>(arranque)
  const [busqueda, setBusqueda] = useState('')
  /* La mesa cuya planilla está desplegada. Una a la vez: cada planilla abierta
     es un teléfono preguntando cada veinte segundos. */
  const [conManos, setConManos] = useState<string | null>(null)
  const miMesa = useRef<HTMLDivElement | null>(null)
  const tira = useRef<HTMLElement | null>(null)
  const elegida = useRef<HTMLButtonElement | null>(null)

  /* Si el torneo avanza mientras la pestaña está abierta —se arma una ronda
     nueva— la elegida sigue siendo válida y no se toca. Lo que sí se corrige es
     entrar sin ninguna y que después aparezcan. */
  useEffect(() => {
    if (rondaId === null && arranque !== null) setRondaId(arranque)
  }, [rondaId, arranque])

  /*
   * La ficha de la ronda elegida, centrada en la tira.
   *
   * En un torneo de nueve rondas la que interesa es la última, y en el ancho de
   * un teléfono entran cuatro: sin esto, la pestaña abre mostrando la ronda 1 y
   * la que se está jugando queda fuera de la pantalla, con la lista de mesas de
   * esa ronda debajo. Se mueve `scrollLeft` a mano y no con `scrollIntoView`
   * porque aquel también desplaza la página en vertical.
   */
  useEffect(() => {
    const caja = tira.current
    const boton = elegida.current
    if (!caja || !boton) return
    caja.scrollLeft = boton.offsetLeft - (caja.clientWidth - boton.offsetWidth) / 2
  }, [rondaId])

  const pedir = useCallback(async (): Promise<MesaPublica[]> => {
    if (!rondaId) return []
    const repo = await obtenerRepositorio()
    return publico
      ? repo.mesasDeRondaPublicas(torneoId, rondaId)
      : repo.listarMesasDeRonda(torneoId, rondaId)
  }, [torneoId, rondaId, publico])

  const { datos, error, cargando, recargar, refrescar } = useCarga(pedir)

  const espera = publico ? ESPERA_CACHE_PUBLICA_MS : 0
  const porManos = useLlamadaEspaciada(
    refrescar,
    publico ? FRENO_MANOS_PUBLICO_MS : FRENO_MANOS_MS,
    espera,
  )
  const porRonda = useLlamadaEspaciada(refrescar, FRENO_RONDA_MS, espera)
  useAvisosDelTorneo(torneoId, publico, (aviso) => {
    if (aviso.tipo === 'mesa') {
      if (aviso.rondaId === rondaId) porManos()
      return
    }
    if (aviso.tipo === 'ronda' && aviso.rondaId !== rondaId) return
    porRonda()
  })

  const buscadas = useMemo(() => {
    const mesas = datos ?? []
    const aguja = normalizar(busqueda.trim())
    if (!aguja) return mesas
    return mesas.filter(
      (m) =>
        String(m.numero) === aguja ||
        m.jugadores.some(
          (j) => normalizar(j.nombre).includes(aguja) || String(j.numero ?? '') === aguja,
        ),
    )
  }, [datos, busqueda])

  const buscarme = useCallback(() => {
    miMesa.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [])

  const ronda = rondas.find((r) => r.rondaId === rondaId) ?? null
  const tengoMesa = (datos ?? []).some((m) => m.esMia)

  if (rondas.length === 0 || rondas.every((r) => r.mesas === 0)) {
    return (
      <Vacio titulo="Todavía no hay mesas">
        <p>
          Las mesas aparecen acá en cuanto el organizador arma la primera ronda, con todos los que
          hicieron check-in.
        </p>
      </Vacio>
    )
  }

  return (
    <div className="mesas-ronda">
      {/* El selector de ronda: una tira de botones que se arrastra de costado.
          Es la navegación de esta pestaña, así que va primero y pegada arriba,
          donde sigue estando después de bajar por veinte mesas. */}
      <nav className="rondas-tira" aria-label="Rondas del torneo" ref={tira}>
        {rondas.map((r) => (
          <button
            key={r.rondaId}
            ref={r.rondaId === rondaId ? elegida : undefined}
            className={`ronda-chip ${r.rondaId === rondaId ? 'activa' : ''} ${
              r.estado === 'cerrada' ? 'cerrada' : r.mesas > 0 ? 'abierta' : 'pendiente'
            }`}
            aria-current={r.rondaId === rondaId ? 'page' : undefined}
            disabled={r.mesas === 0}
            onClick={() => setRondaId(r.rondaId)}
            title={r.mesas === 0 ? `La ronda ${r.numero} todavía no se armó` : undefined}
          >
            R{r.numero}
          </button>
        ))}
      </nav>

      {ronda && (
        <p className="mesas-resumen">
          <span>{plural(ronda.mesas, 'mesa', 'mesas')}</span>
          {ronda.estado === 'cerrada' ? (
            <span className="pastilla cerrado">Ronda cerrada</span>
          ) : (
            <span className="pastilla vivo">
              {ronda.mesasCargadas} de {ronda.mesas} cargadas
            </span>
          )}
          <button className="mesas-refrescar" onClick={recargar} title="Volver a pedir las mesas">
            <Icono nombre="refrescar" tamano={15} />
            Actualizar
          </button>
        </p>
      )}

      <div className="mesas-buscador">
        <Icono nombre="buscar" tamano={16} />
        <input
          type="search"
          inputMode="search"
          value={busqueda}
          placeholder="Buscar por nombre, número o mesa"
          aria-label="Buscar a alguien entre las mesas de la ronda"
          onChange={(e) => setBusqueda(e.target.value)}
        />
      </div>

      {tengoMesa && !busqueda && (
        <button className="boton-buscarme" onClick={buscarme}>
          <Icono nombre="flecha" tamano={16} />
          Ir a mi mesa
        </button>
      )}

      {error && <Problema mensaje={error} alReintentar={recargar} />}
      {!datos && cargando && <Cargando que="Buscando las mesas de la ronda…" />}

      {datos && buscadas.length === 0 && (
        <Vacio titulo={busqueda ? 'Nadie con ese nombre' : 'La ronda todavía no tiene mesas'}>
          {busqueda ? (
            <p>
              No hay ningún jugador ni mesa que coincida con «{busqueda}» en la ronda{' '}
              {ronda?.numero}.
            </p>
          ) : (
            <p>Se armó la ronda pero todavía no se repartieron las mesas.</p>
          )}
        </Vacio>
      )}

      <div className="mesas-lista">
        {buscadas.map((m) => (
          <div key={m.mesaId} ref={m.esMia ? miMesa : undefined}>
            <MesaEnJuego
              mesa={m}
              manosAbiertas={conManos === m.mesaId}
              alVerManos={() => setConManos((abierta) => (abierta === m.mesaId ? null : m.mesaId))}
              {...(alAnotar && m.esMia && !publico ? { alAnotar: () => alAnotar(m.mesaId) } : {})}
            />
            {conManos === m.mesaId && (
              <PlanillaDeMesa
                torneoId={torneoId}
                mesaId={m.mesaId}
                publico={publico}
                {...(alAnotar && m.esMia ? { alAnotar: () => alAnotar(m.mesaId) } : {})}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
